import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { getPayrollPeriodSettings } from '../lib/payrollPeriodSettings';
import { getDueAndOverduePayrollPeriods } from '../lib/payrollPastDuePeriods';
import type { PastDuePayrollPeriod } from '../lib/payrollPastDuePeriods';
import { buildPayrollPeriodRequiredActions } from '../lib/payroll/requiredActions';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';

// Persisted with a reviver because PastDuePayrollPeriod carries Date fields that JSON
// serialization flattens to ISO strings.
const payrollPeriodsCache = createBusinessCache<PastDuePayrollPeriod[]>({
  persistKey: 'payroll-periods',
  reviveStored: (parsed) =>
    (Array.isArray(parsed) ? parsed : []).map((p) => {
      const period = p as Omit<PastDuePayrollPeriod, 'start' | 'end'> & {
        start: string | Date;
        end: string | Date;
      };
      return {
        ...period,
        start: new Date(period.start),
        end: new Date(period.end),
      } as PastDuePayrollPeriod;
    }),
});

/** Due and overdue payroll periods with unpaid hours for the control-centre inbox. */
export function usePayrollPeriodRequiredActions(enabled = true) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [periods, setPeriods] = useState<PastDuePayrollPeriod[]>(
    () => payrollPeriodsCache.get(businessId) ?? []
  );
  const [isLoading, setIsLoading] = useState(() => !payrollPeriodsCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled || !businessId) {
      setPeriods([]);
      setIsLoading(false);
      return;
    }

    // Stale-while-revalidate: this is the heaviest source (scans up to 24 periods),
    // so showing the cached result instantly is the biggest perceived win.
    const hasCache = payrollPeriodsCache.has(businessId);
    if (hasCache) {
      setPeriods(payrollPeriodsCache.get(businessId) ?? []);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      const settings = await getPayrollPeriodSettings(businessId);
      if (!settings) {
        setPeriods([]);
        payrollPeriodsCache.set(businessId, []);
        return;
      }

      const duePeriods = await getDueAndOverduePayrollPeriods(businessId, settings);
      setPeriods(duePeriods);
      payrollPeriodsCache.set(businessId, duePeriods);
    } catch (err) {
      console.error('Error loading payroll period required actions:', err);
      setError('Failed to load payroll periods.');
      setPeriods([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setPeriods([]);
      return;
    }
    void load();
  }, [load, enabled]);

  const payrollPeriodActions = useMemo(
    () => buildPayrollPeriodRequiredActions(periods),
    [periods]
  );

  return {
    periods,
    payrollPeriodActions,
    isLoading,
    error,
    refresh: load,
  };
}
